// Angular
import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
// Service
import { OrderActivityService } from '../../service/order-activity.service';
// Model
import { OrderActivity } from './model/order-activity.model';
// Other
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
	selector: 'cv-order-activity-dialog',
	templateUrl: 'order-activity-dialog.component.html',
	styleUrls: ['order-activity-dialog.component.scss'],
})
export class OrderActivitDialogComponent implements OnInit {
	@ViewChildren('messageRef') messageRefs!: QueryList<ElementRef>;
	public activities: OrderActivity[] = [];
	isLoading = true;
	expandedMap: Record<number, boolean> = {};
	clampMap: Record<number, boolean> = {};
	readonly CLAMP_LINE_HEIGHT = 20;
	readonly CLAMP_LINES = 2;
	readonly CLAMP_HEIGHT = this.CLAMP_LINE_HEIGHT * this.CLAMP_LINES;
	constructor(
		public config: DynamicDialogConfig,
		public ref: DynamicDialogRef,
		private orderActivityService: OrderActivityService
	) { }

	ngOnInit(): void {
		const orderId = this.config.data?.orderId;
		if (!orderId) {
			this.isLoading = false;
			return;
		}

		this.orderActivityService.getOrderActivity(orderId).subscribe({
			next: (res) => {
				this.activities = res.data ?? [];
				this.isLoading = false;

				// wait for DOM to update
				setTimeout(() => this.evaluateClampHeights(), 100);
			},
			error: () => {
				this.isLoading = false;
			},
		});
	}

	/** 
	 * Checks the rendered height of each message and sets clampMap
	 * to true if it exceeds the defined collapsed height. 
	 */
	private evaluateClampHeights(): void {
		this.messageRefs?.forEach((elRef, index) => {
			const el = elRef.nativeElement;
			const actualHeight = el.scrollHeight;
			this.clampMap[index] = actualHeight > this.CLAMP_HEIGHT;
		});
	}

	/**
	 * Toggles the expanded/collapsed state for the message at the given index.
	 * When expanded, the full message is shown; otherwise, it is clamped to a fixed height.
	 *
	 * @param index Index of the activity message to toggle
	 */
	toggleExpand(index: number): void {
		this.expandedMap[index] = !this.expandedMap[index];
	}

	/**
	 * Closes the order activity dialog.
	 * This method is triggered when the user clicks the "Ok" or close button.
	 */
	close(): void {
		this.ref.close();
	}

	/**
	 * Converts the given HTML string into plain text for collapsed view.
	 * 
	 * @param html - The original message string that may contain HTML
	 * @param index - The index of the message (currently unused)
	 * @returns The plain text version of the message
	 */
	getClampedMessage(html: string, index: number): string {
		if (!html) return '-';

		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = html;
		return tempDiv.textContent || tempDiv.innerText || '';
	}

	/**
	 * Copies the plain text content of the given HTML message to the clipboard.
	 * 
	 * @param message - The message string that may contain HTML
	 */
	copyMessage(message: string): void {
		// Create a temporary element to strip HTML tags if necessary
		const tempElement = document.createElement('div');
		tempElement.innerHTML = message || '';
		const text = tempElement.innerText;

		navigator.clipboard.writeText(text).catch(err => {
			console.error('Failed to copy message: ', err);
		});
	}
}
